import { useEffect } from "react";

// ---------------------------------------------------------------------------
// Three-dot typing indicator. Shown in the assistant bubble while a turn
// is mid-stream and no text has arrived yet (e.g. while a tool is running).
//
// Pure CSS — keyframes are injected once on first mount. Stays independent
// of Tailwind / shadcn so the panel renders consistently across themes.
// ---------------------------------------------------------------------------

const KEYFRAMES_ID = "xale-ai-typing-dots-keyframes";
const KEYFRAMES = `
@keyframes xaleTypingDot {
  0%, 60%, 100% { transform: translateY(0);   opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1;    }
}
@keyframes xaleAiShimmer {
  0%   { background-position: -120px 0; }
  100% { background-position: 240px 0;  }
}
`;

function ensureKeyframesInjected() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const el = document.createElement("style");
  el.id = KEYFRAMES_ID;
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

const dotBase: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--color-success, #156548)",
  display: "inline-block",
  animation: "xaleTypingDot 1.1s ease-in-out infinite",
};

export default function TypingDots({ label }: { label?: string }) {
  useEffect(() => {
    ensureKeyframesInjected();
  }, []);

  return (
    <span
      role="status"
      aria-label={label || "Thinking"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 0",
      }}
    >
      <span style={{ ...dotBase, animationDelay: "0ms" }} />
      <span style={{ ...dotBase, animationDelay: "160ms" }} />
      <span style={{ ...dotBase, animationDelay: "320ms" }} />
      {label && (
        <span
          style={{
            fontSize: 11,
            color: "var(--color-text-secondary, #6b7280)",
            marginLeft: 4,
            fontStyle: "italic",
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}
